import React, { useEffect, useState } from 'react';
import { Code2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DataRoomCodebase = ({ token, onMetrics }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/dataroom/codebase-metrics`, { headers: { 'X-Dataroom-Token': token } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { setData(d); if (d) onMetrics?.(d); })
      .catch(() => {});
  }, [token]);

  if (!data?.totals) return null;
  const t = data.totals;
  const tiles = [
    { value: t.everything?.lines, label: 'Full Project · Lines', sub: 'code + templates + data + mobile + docs', bg: 'bg-[#F4F1FB] border-[#7B4FD8]/25', color: 'text-[#5B32C8]', accent: 'text-[#7B4FD8]' },
    { value: t.active_code?.lines, label: 'Active Code · Lines', sub: 'live code, excl. archive & docs', bg: 'bg-emerald-50 border-emerald-200/70', color: 'text-emerald-700', accent: 'text-emerald-600' },
    { value: data.live_endpoints, label: 'Live API Endpoints', sub: `${data.api_route_files} route modules`, bg: 'bg-slate-50 border-slate-200', color: 'text-slate-700', accent: 'text-slate-500' },
    { value: t.everything?.files, label: 'Source Files', sub: `${data.service_files} services · ${data.worker_files} workers`, bg: 'bg-white border-[#7B4FD8]/25', color: 'text-[#1A1209]', accent: 'text-[#7B4FD8]' },
  ];

  return (
    <div id="sec-codebase" className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm scroll-mt-16" data-testid="dr-codebase">
      <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2">
        <Code2 size={14} className="text-[#7B4FD8]" /> Codebase — Live Breakdown
      </h3>
      <p className="text-[11px] text-gray-500 mb-1">
        Counted live from the running system, not a static claim — recounted {data.computed_at ? new Date(data.computed_at).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}.
      </p>
      {data.growth && (data.growth.lines_added !== 0 || data.growth.endpoints_added !== 0) && (
        <p className="text-[11px] font-bold text-emerald-700 mb-4" data-testid="dr-codebase-growth">
          ▲ {data.growth.lines_added >= 0 ? '+' : ''}{Number(data.growth.lines_added).toLocaleString()} lines · {data.growth.endpoints_added >= 0 ? '+' : ''}{data.growth.endpoints_added} endpoints since the last release ({new Date(data.growth.since).toLocaleDateString('en-GB')}) — every release linted, reviewed and held to an acquisition-grade code standard.
        </p>
      )}
      {(!data.growth || (data.growth.lines_added === 0 && data.growth.endpoints_added === 0)) && <div className="mb-3" />}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {tiles.map((tile, i) => (
          <div key={i} className={`rounded-xl p-4 border ${tile.bg}`} data-testid={`dr-codebase-tile-${i}`}>
            <p className={`text-xl font-black ${tile.color}`}>{Number(tile.value || 0).toLocaleString()}</p>
            <p className={`text-[9px] font-bold uppercase tracking-wider mt-1 ${tile.accent}`}>{tile.label}</p>
            <p className="text-[9px] text-gray-400 mt-0.5">{tile.sub}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] min-w-[640px]">
          <thead><tr className="text-[9px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
            <th className="text-left py-2 pr-2 font-bold">Component</th>
            <th className="text-left py-2 px-2 font-bold">What it is</th>
            <th className="text-center py-2 px-2 font-bold">Files</th>
            <th className="text-center py-2 pl-2 font-bold">Lines</th>
          </tr></thead>
          <tbody>
            {(data.breakdown || []).map((row, i) => (
              <tr key={row.key || i} className="border-b border-gray-50" data-testid={`dr-codebase-row-${row.key}`}>
                <td className="py-2.5 pr-2">
                  <p className="font-black text-gray-800">{row.label}</p>
                  <p className="text-[10px] text-gray-400">{row.group}</p>
                </td>
                <td className="py-2.5 px-2 text-gray-600">{row.description}</td>
                <td className="py-2.5 px-2 text-center text-gray-600 tabular-nums">{Number(row.files || 0).toLocaleString()}</td>
                <td className="py-2.5 pl-2 text-center font-bold text-gray-800 tabular-nums">{Number(row.lines || 0).toLocaleString()}</td>
              </tr>
            ))}
            <tr className="border-b border-gray-50">
              <td className="py-2.5 pr-2 font-black text-gray-800" colSpan={3}>Active codebase (excl. archive &amp; docs)</td>
              <td className="py-2.5 pl-2 text-center font-black text-gray-800 tabular-nums">{Number(t.active_code?.lines || 0).toLocaleString()}</td>
            </tr>
            <tr className="border-b border-gray-50">
              <td className="py-2.5 pr-2 text-gray-600" colSpan={3}>Including legacy / archived code</td>
              <td className="py-2.5 pl-2 text-center text-gray-600 tabular-nums">{Number(t.with_legacy?.lines || 0).toLocaleString()}</td>
            </tr>
            <tr>
              <td className="py-2.5 pr-2 font-black text-[#5B32C8] uppercase" colSpan={3}>Full project total</td>
              <td className="py-2.5 pl-2 text-center font-black text-[#5B32C8] tabular-nums">{Number(t.everything?.lines || 0).toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-gray-700 mt-4 leading-relaxed border-l-4 border-[#7B4FD8]/30 pl-3">
        For technical diligence: every figure above is recounted from the live source tree and running API on demand — the same numbers the founder sees in the Command Centre.
      </p>
    </div>
  );
};

export default DataRoomCodebase;
